import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { rand, dateLabel, StatusPill } from '@/components/stokvel/format';

const CAT = { medical: 'Qualified Medical Emergency', funeral: 'Funeral / Death in Immediate Family', housing: 'Critical Housing Loss / Catastrophic Event' };

const DecisionDialog = ({ item, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [payoutDate, setPayoutDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) { setAmount(String(item.amount_requested)); setPayoutDate(new Date().toISOString().slice(0, 10)); setReason(''); }
  }, [item]);
  const [reasonErr, setReasonErr] = useState(false);
  const decide = async (action) => {
    if (action === 'decline' && !reason.trim()) {
      setReasonErr(true);
      toast.error('Please enter a reason for declining');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/stokvel/admin/withdrawals/${item.id}/decision`, {
        action, reason, approved_amount: Number(amount), payout_date: payoutDate,
      });
      toast.success(action === 'approve' ? 'Withdrawal approved' : 'Withdrawal declined');
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="stokvel-wd-decision-dialog">
        <DialogHeader><DialogTitle>Emergency withdrawal — {item?.name}</DialogTitle></DialogHeader>
        {item && (
          <div className="space-y-3 text-sm">
            <div className="bg-stone-50 rounded p-3 space-y-1">
              <div><b>{CAT[item.category]}</b> · requested {rand(item.amount_requested)}</div>
              <div className="text-xs text-stone-600">Capital at request {rand(item.capital_at_request)} · max allowed {rand(item.max_at_request)}</div>
              <p className="text-xs text-stone-700 whitespace-pre-wrap">"{item.motivation}"</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Approved amount (R)</Label><Input type="number" value={amount} max={item.max_at_request} onChange={e => setAmount(e.target.value)} data-testid="stokvel-wd-approved-amount-input" /></div>
              <div><Label className="text-xs">Payout date</Label><Input type="date" value={payoutDate} onChange={e => setPayoutDate(e.target.value)} data-testid="stokvel-wd-payout-date-input" /></div>
            </div>
            <div><Label className="text-xs">Reason / note (required to decline)</Label><Textarea rows={3} value={reason} onChange={e => { setReason(e.target.value); setReasonErr(false); }}
              className={reasonErr ? 'border-rose-500 ring-1 ring-rose-300' : ''} data-testid="stokvel-wd-reason-input" />
              {reasonErr && <p className="text-xs text-rose-600 mt-1" data-testid="stokvel-wd-reason-error">A reason is required to decline.</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="text-rose-700" disabled={busy} onClick={() => decide('decline')} data-testid="stokvel-wd-decline-btn">Decline</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={busy || !(Number(amount) > 0) || !payoutDate} onClick={() => decide('approve')} data-testid="stokvel-wd-approve-btn">Approve &amp; confirm payout</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const StokvelWithdrawalsPanel = () => {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/stokvel/admin/withdrawals`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load withdrawals'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="stokvel-withdrawals-table">
        <thead className="bg-stone-100 text-xs uppercase text-stone-600">
          <tr><th className="text-left px-3 py-2">Requested</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Category</th><th className="text-right px-3 py-2">Requested</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Approved</th><th className="text-left px-3 py-2">Payout</th><th className="px-3 py-2"></th></tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map(w => (
            <tr key={w.id} data-testid={`stokvel-admin-wd-${w.id}`}>
              <td className="px-3 py-2">{dateLabel(w.created_at)}</td>
              <td className="px-3 py-2 font-medium">{w.name}</td>
              <td className="px-3 py-2">{CAT[w.category]}</td>
              <td className="px-3 py-2 text-right font-mono">{rand(w.amount_requested)}</td>
              <td className="px-3 py-2"><StatusPill status={w.status} />{w.status === 'declined' && <div className="text-xs text-rose-700">{w.decision_reason}</div>}</td>
              <td className="px-3 py-2 text-right font-mono">{w.status === 'approved' ? rand(w.approved_amount) : '—'}</td>
              <td className="px-3 py-2">{w.payout_date ? dateLabel(w.payout_date) : '—'}</td>
              <td className="px-3 py-2 text-right">{w.status === 'pending' && <Button size="sm" onClick={() => setSel(w)} className="bg-stone-800 text-white" data-testid={`stokvel-wd-review-${w.id}`}>Review</Button>}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={8} className="text-center text-stone-400 py-6">No emergency withdrawal requests.</td></tr>}
        </tbody>
      </table>
      <DecisionDialog item={sel} onClose={() => setSel(null)} onDone={load} />
    </div>
  );
};
